import { useCallback, useEffect, useState } from 'react'
import { useElementWidth } from '@/hooks/useElementWidth'
import {
  resolvePaneWidths,
  maxAiPanelWidthFor,
  maxOutlineWidthFor,
  MIN_AI_PANEL_WIDTH,
  DEFAULT_AI_PANEL_WIDTH,
  MIN_OUTLINE_WIDTH,
  DEFAULT_OUTLINE_WIDTH,
} from '@/lib/pane-layout'

// ===================
// Constants
// ===================

/** Shared by every three-pane view, so a width set in one carries to the others. */
export const OUTLINE_WIDTH_STORAGE_KEY = 'outline-width'
export const AI_PANEL_WIDTH_STORAGE_KEY = 'ai-panel-width'

/** `ResizeHandle` is `w-1`. */
export const RESIZE_HANDLE_WIDTH = 4

// ===================
// Storage
// ===================

/**
 * Read a stored pane width. Falls back when storage is unavailable (private
 * mode, quota, SSR) or the stored value is not a number. Range clamping is
 * left to `resolvePaneWidths`, so the stored preference itself is untouched.
 */
export function readStoredWidth(key: string, fallback: number): number {
  try {
    const saved = window.localStorage.getItem(key)
    if (!saved) return fallback
    const parsed = parseInt(saved, 10)
    return Number.isFinite(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function writeStoredWidth(key: string, width: number): void {
  try {
    window.localStorage.setItem(key, String(width))
  } catch {
    // Storage unavailable — the width still applies for this session.
  }
}

// ===================
// Hook
// ===================

export interface UseResizablePanesOptions {
  /** Whether the AI panel (and its resize handle) is rendered. */
  isAiPanelOpen: boolean
  /**
   * Px taken in the row by fixed-width columns other than the two side panes
   * and their handles — e.g. a collapsed AI panel rail. Counted against the
   * centre column's minimum.
   */
  fixedWidth?: number
}

export interface ResizablePanes {
  /** Callback ref for the row holding outline | centre | AI panel. */
  containerRef: (node: HTMLElement | null) => void
  /** Outline width to render, clamped so the centre keeps its minimum. */
  outlineWidth: number
  /** AI panel width to render, clamped so the centre keeps its minimum. */
  aiPanelWidth: number
  isOutlineResizing: boolean
  isAiPanelResizing: boolean
  onOutlineResizeStart: (e: React.MouseEvent) => void
  onAiPanelResizeStart: (e: React.MouseEvent) => void
}

/**
 * Width state + drag logic for the three-pane shell: outline rail on the left,
 * AI panel on the right, a centre column taking the remainder.
 *
 * Unlike `useResizablePanel` (one pane, fixed bounds), the two panes here are
 * coupled: each one's drag cap depends on the other's rendered width and the
 * measured row, so the centre always keeps `MIN_EDITOR_WIDTH` when the
 * viewport can afford it (see `@/lib/pane-layout`).
 */
export function useResizablePanes({ isAiPanelOpen, fixedWidth = 0 }: UseResizablePanesOptions): ResizablePanes {
  const [aiPanelWidth, setAiPanelWidth] = useState(() =>
    readStoredWidth(AI_PANEL_WIDTH_STORAGE_KEY, DEFAULT_AI_PANEL_WIDTH),
  )
  const [outlineWidth, setOutlineWidth] = useState(() =>
    readStoredWidth(OUTLINE_WIDTH_STORAGE_KEY, DEFAULT_OUTLINE_WIDTH),
  )
  const [isAiPanelResizing, setIsAiPanelResizing] = useState(false)
  const [isOutlineResizing, setIsOutlineResizing] = useState(false)
  const { ref: containerRef, element: containerEl, width: containerWidth } = useElementWidth()

  // Keep the centre column readable: the side panes give width back when the
  // window can't afford all three at their stored sizes (F19). Stored
  // preferences are untouched — widen the window and they come straight back.
  // Resolved here (not at render time) so the drag caps below can be fed the
  // widths actually on screen rather than the stored ones.
  const handlesWidth = (isAiPanelOpen ? 2 * RESIZE_HANDLE_WIDTH : RESIZE_HANDLE_WIDTH) + fixedWidth
  const { outlineWidth: renderedOutlineWidth, aiPanelWidth: renderedAiPanelWidth } = resolvePaneWidths({
    containerWidth,
    outlineWidth,
    aiPanelWidth: isAiPanelOpen ? aiPanelWidth : null,
    handlesWidth,
  })
  const effectiveOutlineWidth = renderedOutlineWidth ?? outlineWidth
  const effectiveAiPanelWidth = renderedAiPanelWidth ?? aiPanelWidth

  useEffect(() => {
    writeStoredWidth(AI_PANEL_WIDTH_STORAGE_KEY, aiPanelWidth)
  }, [aiPanelWidth])

  useEffect(() => {
    writeStoredWidth(OUTLINE_WIDTH_STORAGE_KEY, outlineWidth)
  }, [outlineWidth])

  const onAiPanelResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsAiPanelResizing(true)
  }, [])

  const onOutlineResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsOutlineResizing(true)
  }, [])

  useEffect(() => {
    const activeResize = isAiPanelResizing || isOutlineResizing

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerEl) return

      const containerRect = containerEl.getBoundingClientRect()

      if (isAiPanelResizing) {
        // Cap the drag at the width that still leaves the centre its
        // minimum, so the handle keeps tracking the cursor instead of
        // drifting away from a panel that has stopped growing.
        const max = maxAiPanelWidthFor(containerRect.width, effectiveOutlineWidth, handlesWidth)
        const newWidth = containerRect.right - e.clientX
        setAiPanelWidth(Math.max(MIN_AI_PANEL_WIDTH, Math.min(max, newWidth)))
      }

      if (isOutlineResizing) {
        // Only reserve room for the AI panel when it is actually rendered —
        // otherwise closing the panel to make room would leave the rail
        // un-resizable at exactly the widths where it matters most.
        const max = maxOutlineWidthFor(
          containerRect.width,
          isAiPanelOpen ? effectiveAiPanelWidth : 0,
          handlesWidth,
        )
        const newWidth = e.clientX - containerRect.left
        setOutlineWidth(Math.max(MIN_OUTLINE_WIDTH, Math.min(max, newWidth)))
      }
    }

    const handleMouseUp = () => {
      setIsAiPanelResizing(false)
      setIsOutlineResizing(false)
    }

    if (activeResize) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [
    isAiPanelResizing,
    isOutlineResizing,
    containerEl,
    handlesWidth,
    isAiPanelOpen,
    effectiveOutlineWidth,
    effectiveAiPanelWidth,
  ])

  return {
    containerRef,
    outlineWidth: effectiveOutlineWidth,
    aiPanelWidth: effectiveAiPanelWidth,
    isOutlineResizing,
    isAiPanelResizing,
    onOutlineResizeStart,
    onAiPanelResizeStart,
  }
}
